import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnchorProps } from '../../../../../shared/Anchor';
import { DiscoveryArticle } from '../../../../../../store/features/discovery';
import { useHubContext } from '../../../context/hubContext';
import { getDiscoveryTopicsByBrand } from '../../../helpers';
import { createCanonicalUrl } from '../../../../../../helpers';
import { useRootSelector } from '../../../../../../store/hooks';
import { selectPageConfig } from '../../../../../../store/features/pageConfig';
import { getArticlePublicationDate } from '../../../../../../utils/getArticlePublicationDate';
import {
  CardAnchor,
  Image,
  Title,
  Badge,
  TopicsList,
  CardButton,
  TitleProps,
  ImageProps,
  CardButtonProps,
} from '../components';
import {
  ProductCard,
  ProductListCard,
  ProductInlineCard,
} from '../../products';
import { TopicsListType } from '../SiteCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import imageCover from '../../../../../../assets/images/default-image.png';

import styles from './ArticleCard.module.scss';

export enum ArticleCardTypes {
  default = 'default',
  featured = 'featured',
  list = 'list',
  inline = 'inline',
}

export interface ArticleCardProps extends Omit<AnchorProps, 'to'> {
  data: DiscoveryArticle;
  cardType?: keyof typeof ArticleCardTypes;
  showFilter?: boolean;
  showPublicationDate?: boolean;
}

export const ArticleCard = (props: ArticleCardProps) => {
  const {
    data: {
      path,
      title,
      imageUrl: imagePath,
      topics = [],
      summary,
      datePublished,
      dateModified,
      externalLinkEnabled,
      externalLink,
    },
    cardType = ArticleCardTypes.default,
    showFilter,
    showPublicationDate,
    ...restProps
  } = props;
  const { i18n } = useTranslation();
  const { brandName, showArticlePublicationDates } = useHubContext();
  const { parentDomain } = useRootSelector(selectPageConfig);
  const topicsList = useMemo(
    () => getDiscoveryTopicsByBrand(topics, brandName),
    [topics, brandName],
  );

  const publicationDate = getArticlePublicationDate(
    datePublished,
    showPublicationDate ?? Boolean(showArticlePublicationDates),
    i18n.language,
  );

  const getImage = (style?: ImageProps['imageStyle']) => (
    <Image imageStyle={style} path={imagePath} fallback={imageCover} />
  );
  const getTitle = (
    size?: TitleProps['size'],
    className?: TitleProps['className'],
  ) => (
    <Title size={size} className={className}>
      {title}{' '}
      {externalLinkEnabled && (
        <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
      )}
    </Title>
  );
  const getTopicsList = ({ count, ...props }: TopicsListType) =>
    topicsList.length > 0 && (
      <TopicsList list={topicsList.slice(0, count)}>
        {(topic) => (
          <Badge theme="outlined" size="tiny" {...props}>
            {topic}
          </Badge>
        )}
      </TopicsList>
    );
  const getShowMoreBadge = (size?: CardButtonProps['size']) => (
    <CardButton size={size}>Read More</CardButton>
  );

  const canonical = createCanonicalUrl(imagePath, parentDomain);
  const imageUrl = canonical ? canonical.replace(/\/$/, '') : '';

  return (
    <CardAnchor {...restProps} to={externalLinkEnabled ? externalLink : path}>
      {cardType === ArticleCardTypes.default && (
        <ProductCard
          image={getImage()}
          title={getTitle('extraSmall')}
          topics={getTopicsList({ count: 1 })}
          date={publicationDate}
          leftFooter={getShowMoreBadge()}
        />
      )}
      {cardType === ArticleCardTypes.featured && (
        <ProductListCard
          image={getImage('list')}
          imageSize={showFilter ? 'medium' : 'small'}
          title={getTitle('large')}
          leftHeader={getTopicsList({ count: 3 })}
          rightHeader={<Badge size="medium">Featured</Badge>}
          date={publicationDate}
          showMoreBadge={getShowMoreBadge()}
        />
      )}
      {cardType === ArticleCardTypes.list && (
        <ProductListCard
          image={getImage('list')}
          title={getTitle()}
          leftHeader={getTopicsList({ count: 3 })}
          date={publicationDate}
          showMoreBadge={getShowMoreBadge()}
        />
      )}
      {cardType === ArticleCardTypes.inline && (
        <ProductInlineCard
          leftHeader={getTopicsList({
            theme: 'text',
            className: styles.inlineTopic,
            size: 'small',
            count: 1,
          })}
          title={getTitle('extraSmall', styles.inlineTitle)}
          date={publicationDate}
        />
      )}
      <div itemScope itemType="https://schema.org/Article">
        <meta
          itemProp="mainEntityOfPage"
          content={createCanonicalUrl(path, parentDomain)}
        />
        <meta itemProp="datePublished" content={datePublished} />
        <meta itemProp="dateModified" content={dateModified} />
        <meta itemProp="image.url" content={imageUrl} />
        <meta itemProp="description" content={summary} />
      </div>
    </CardAnchor>
  );
};
